import { Badge, Stack } from "@mui/material";
import { SectionHeader } from "../../../components/SectionHeader";
import {
  Spacing,
  Button,
  Shadow,
  FoundationColorTokens,
  Typography as LeoTypography,
  Breadcrumb,
  Typography,
  CornerRadius,
} from "@surya-digital/leo-reactjs-material-ui";
import { ChevronDown, ChevronUp, FilePieChart } from "lucide-react";
import React, { useState } from "react";
import { TFunction } from "i18next";
import { observer } from "mobx-react";
import { getSnapshot, Instance } from "mobx-state-tree";
import { SurveyViewDetailsStore } from "../stores/SurveyViewDetailsStore";
import { mapRespondentFiltersToFilterComponentFilters } from "../../../components/filter/utils/getRespondentFilterList";
import { DashboardFilterComponent } from "./DashboardFilterComponent";
import { BorderStyle, EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  NavigateToFunctions,
  processSurveyParams,
  processSurveyViewParams,
} from "../../../routes/RoutesHelper";
import { FeatureInDevelopmentSnackbar } from "@pulse/shared-components";
import { FilterStore } from "../../../components/filter/store/FilterStore";
import { ManageQuestionViews } from "./manageQuestionViews/ManageQuestionViews";
import { ManageQuestionViewsStore } from "../stores/ManageQuestionViewsStore";
import { SignInStore } from "../../auth/store/SignInStore";
import { UserPrivileges } from "../../store/user/UserPrivileges";
import { DownloadDashboardViewPDF } from "./DownloadDashboardViewPDF";
import { DownloadDashboardViewPDFStore } from "../stores/DownloadDashboardViewPDFStore";
import { TableFilter } from "@pulse/pulse-rpcs/lib/types/tableFilter";

interface ActionElementProps {
  spacing: Spacing;
  isFilterContainerVisible: boolean;
  setIsFilterContainerVisible: (isFilterContainerVisible: boolean) => void;
  t: TFunction;
  filterStore: Instance<typeof FilterStore>;
  tokens: FoundationColorTokens<string>;
  manageQuestionViewsStore: Instance<typeof ManageQuestionViewsStore>;
  downloadDashboardViewPDFStore: Instance<typeof DownloadDashboardViewPDFStore>;
  navigateTo: NavigateToFunctions;
  border: BorderStyle;
  typography: Typography;
  cornerRadius: CornerRadius;
  signInStore: Instance<typeof SignInStore>;
  viewName: string | null;
  tableFilters: TableFilter[];
  isNoQuestionViewPresent: boolean;
  isMasterView: boolean;
  scrollToTop: () => void;
}

export const ActionElement = observer(
  ({
    spacing,
    isFilterContainerVisible,
    setIsFilterContainerVisible,
    t,
    filterStore,
    tokens,
    manageQuestionViewsStore,
    navigateTo,
    border,
    typography,
    cornerRadius,
    signInStore,
    downloadDashboardViewPDFStore,
    viewName,
    tableFilters,
    isNoQuestionViewPresent,
    isMasterView,
    scrollToTop,
  }: ActionElementProps): React.ReactElement => {
    const [
      isFeatureInDevelopmentSnackbarVisible,
      setIsFeatureInDevelopmentSnackbarVisible,
    ] = useState(false);

    const ManageFiltersButton = (): React.ReactElement => {
      return (
        <Button
          name="manageFilters"
          variant={
            filterStore.areNoFiltersApplied
              ? "outlined-neutral"
              : "outlined-color"
          }
          size="small"
          label={
            filterStore.areNoFiltersApplied
              ? t("filters.addFilter")
              : t("surveyDashboard.manageFilters")
          }
          onClick={(): void => {
            setIsFilterContainerVisible(!isFilterContainerVisible);
          }}
          icon={isFilterContainerVisible ? <ChevronUp /> : <ChevronDown />}
          iconPosition="trailing"
        />
      );
    };

    return (
      <Stack spacing={spacing.spaceSM} direction="row">
        <DownloadDashboardViewPDF
          t={t}
          navigateTo={navigateTo}
          spacing={spacing}
          tokens={tokens}
          typography={typography}
          downloadDashboardViewPDFStore={downloadDashboardViewPDFStore}
          viewName={viewName}
          tableFilters={tableFilters}
          isDownloadDashboardViewPDFButtonDisabled={isNoQuestionViewPresent}
        />
        {signInStore.isPrivilegeGranted(
          UserPrivileges.processSurveyDashboardViews,
        ) &&
          !isMasterView && (
            <ManageQuestionViews
              t={t}
              manageQuestionViewsStore={manageQuestionViewsStore}
              navigateTo={navigateTo}
              spacing={spacing}
              border={border}
              cornerRadius={cornerRadius}
              tokens={tokens}
              typography={typography}
              scrollToTop={scrollToTop}
            />
          )}
        {filterStore.areNoFiltersApplied ? (
          <ManageFiltersButton />
        ) : (
          <Badge
            variant="dot"
            color="secondary"
            badgeContent={EMPTY_CHARACTER}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: tokens.backgroundPrimary,
              },
            }}
          >
            <ManageFiltersButton />
          </Badge>
        )}
        <FeatureInDevelopmentSnackbar
          isSnackbarVisible={isFeatureInDevelopmentSnackbarVisible}
          setIsSnackbarVisible={setIsFeatureInDevelopmentSnackbarVisible}
        />
      </Stack>
    );
  },
);

interface QuestionViewSectionHeaderProps {
  spacing: Spacing;
  t: TFunction;
  shadow: Shadow;
  surveyViewDetailsStore: Instance<typeof SurveyViewDetailsStore>;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  border: BorderStyle;
  cornerRadius: CornerRadius;
  navigateTo: NavigateToFunctions;
  signInStore: Instance<typeof SignInStore>;
  scrollToTop: () => void;
}

export const QuestionViewSectionHeader = observer(
  ({
    spacing,
    shadow,
    t,
    surveyViewDetailsStore,
    tokens,
    typography,
    border,
    cornerRadius,
    navigateTo,
    signInStore,
    scrollToTop,
  }: QuestionViewSectionHeaderProps): React.ReactElement => {
    const [isFilterContainerVisible, setIsFilterContainerVisible] =
      React.useState(false);

    const getSurveyViewResults = async (): Promise<void> => {
      surveyViewDetailsStore.resetCurrentQuestionViewIds();
      surveyViewDetailsStore.resetIsLastElementReached();
      scrollToTop();
      await processSurveyViewParams(
        async (surveyViewId: string, surveyId: string, projectId: string) => {
          surveyViewDetailsStore.getPaginatedSurveyViewResults(
            surveyViewId,
            surveyId,
            projectId,
            false,
          );
        },
        navigateTo,
      );
    };
    const filterList = surveyViewDetailsStore.respondentColumns.map(
      (respondentColumn) => {
        return {
          key: respondentColumn.name,
          label: respondentColumn.name,
        };
      },
    );

    return (
      <Stack
        position="sticky"
        top={0}
        width="100%"
        zIndex={2}
        bgcolor={tokens.backgroundElevatedLevel1}
      >
        <SectionHeader
          padding={`${spacing.spaceMD} ${spacing.space2XL}`}
          actionElement={
            <ActionElement
              spacing={spacing}
              isFilterContainerVisible={isFilterContainerVisible}
              setIsFilterContainerVisible={setIsFilterContainerVisible}
              t={t}
              filterStore={surveyViewDetailsStore.filterStore}
              tokens={tokens}
              manageQuestionViewsStore={
                surveyViewDetailsStore.manageQuestionViewsStore
              }
              navigateTo={navigateTo}
              border={border}
              cornerRadius={cornerRadius}
              typography={typography}
              signInStore={signInStore}
              downloadDashboardViewPDFStore={
                surveyViewDetailsStore.downloadDashboardViewPDFStore
              }
              viewName={surveyViewDetailsStore.surveyViewName}
              tableFilters={surveyViewDetailsStore.getFilters()}
              isNoQuestionViewPresent={
                surveyViewDetailsStore.isNoQuestionViewPresent
              }
              isMasterView={surveyViewDetailsStore.isMasterView}
              scrollToTop={scrollToTop}
            />
          }
          titleActionElement={
            <Breadcrumb
              links={[
                {
                  label: t("surveyDashboard.views.viewsStringLiteral"),
                  icon: <FilePieChart />,
                  onLabelClick: () =>
                    processSurveyParams(
                      async (surveyId, projectId): Promise<void> => {
                        navigateTo.report(projectId, surveyId);
                      },
                      navigateTo,
                    ),
                },
              ]}
              currentLabel={
                surveyViewDetailsStore.surveyViewName ?? EMPTY_CHARACTER
              }
            />
          }
        />
        {isFilterContainerVisible && (
          <Stack
            padding={`${spacing.spaceMD} ${spacing.space2XL}`}
            width="100%"
            height="100%"
            boxShadow={shadow.level3}
            position="absolute"
            top="65px"
            bgcolor={tokens.backgroundElevatedLevel1}
          >
            <DashboardFilterComponent
              filters={mapRespondentFiltersToFilterComponentFilters(
                filterList,
                [],
                t,
                getSnapshot(
                  surveyViewDetailsStore.surveyViewFilterOptionsByKey,
                ) as Record<string, string[]>,
              )}
              isDisabled={false}
              onApplyFiltersClick={getSurveyViewResults}
              onRemoveFiltersClick={getSurveyViewResults}
              store={surveyViewDetailsStore.filterStore}
              spacing={spacing}
              tokens={tokens}
              typography={typography}
              border={border}
              borderRadius={cornerRadius}
              shadow={shadow}
              t={t}
              onFilterSelected={(filterKey) =>
                surveyViewDetailsStore.fetchSurveyViewFilterOptions(filterKey)
              }
              isFilterOptionsLoading={
                surveyViewDetailsStore.isFetchingSurveyViewFilterOptions
              }
            />
          </Stack>
        )}
      </Stack>
    );
  },
);
